import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useComplexForm } from '../../../hooks/venue/useComplexForm';
import { useComplexDetail } from '../../../hooks/venue/useComplexDetail';
import { useOwnerComplex } from '../../../hooks/venue/useOwnerComplex';
import { theme } from '../../../theme';
import {
  ArrowLeft,
  Save,
  MapPin,
  Phone,
  Mail,
  Globe,
  Car,
  ShieldCheck,
  Home,
  Coffee,
  ShoppingBag,
  Check,
  X,
} from 'lucide-react';

const PROVINCES = [
  'Buenos Aires',
  'CABA',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán',
];

const CreateComplexPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // Detectar si estamos en modo edit (ruta /owner/complex/edit)
  const isEditRoute = location.pathname.includes('/edit');
  const isEditing = Boolean(id) || isEditRoute;

  // Hooks - usar useOwnerComplex si estamos en ruta /edit sin ID
  const { complex: ownerComplex, loading: loadingOwnerComplex, fetchComplex: fetchOwnerComplex } = useOwnerComplex();
  const { complex: detailComplex, loading: loadingDetailComplex, fetchComplex: fetchDetailComplex } = useComplexDetail(id || '');

  // Determinar qué complejo usar
  const complex = id ? detailComplex : (isEditRoute ? ownerComplex : null);
  const loadingComplex = id ? loadingDetailComplex : (isEditRoute ? loadingOwnerComplex : false);
  const fetchComplex = id ? fetchDetailComplex : fetchOwnerComplex;

  const { formData, errors, loading, updateField, submitForm } = useComplexForm(complex || undefined);
  const [newAmenityInput, setNewAmenityInput] = useState('');

  useEffect(() => {
    if (id) {
      fetchDetailComplex();
    } else if (isEditRoute) {
      fetchOwnerComplex();
    }
  }, [id, isEditRoute, fetchDetailComplex, fetchOwnerComplex]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await submitForm();

    if (result.success && result.complex) {
      if (isEditing) {
        alert('Complejo actualizado exitosamente');
        fetchComplex();
      } else {
        alert('Complejo creado exitosamente');
        // Navigate to dashboard after creating complex
        navigate('/owner/dashboard');
      }
    } else {
      alert(result.error || 'Error al guardar el complejo');
    }
  };

  const toggleAmenity = (amenity: keyof typeof formData) => {
    if (typeof formData[amenity] === 'boolean') {
      updateField(amenity as any, !formData[amenity] as any);
    }
  };

  const addAmenity = () => {
    const trimmed = newAmenityInput.trim();
    if (trimmed && !formData.amenities.includes(trimmed)) {
      updateField('amenities', [...formData.amenities, trimmed]);
      setNewAmenityInput('');
    }
  };

  const removeAmenity = (amenityToRemove: string) => {
    updateField('amenities', formData.amenities.filter(a => a !== amenityToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addAmenity();
    }
  };

  const loadingContainerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const loadingTextStyle: React.CSSProperties = {
    fontSize: '18px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
  };

  if (isEditing && loadingComplex) {
    return (
      <div style={loadingContainerStyle}>
        <div style={loadingTextStyle}>Cargando complejo...</div>
      </div>
    );
  }

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderBottom: `1px solid ${theme.colors.border}`,
    padding: '32px 48px',
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    cursor: 'pointer',
    padding: 0,
    marginBottom: '12px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '36px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.primary,
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
    marginBottom: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
    margin: 0,
  };

  const formStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  const twoColumnsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '32px',
    padding: '32px 48px',
    flex: 1,
  };

  const formColumnStyle: React.CSSProperties = {
    width: '700px',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
  };

  const sidebarStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.text,
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    fontWeight: '600',
  };

  const requiredStyle: React.CSSProperties = {
    color: theme.colors.danger,
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '10px',
    height: '48px',
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    width: '100%',
    outline: 'none',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    height: 'auto',
    minHeight: '120px',
    resize: 'vertical',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23B0BEC5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
    paddingRight: '48px',
    cursor: 'pointer',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme.colors.danger,
    fontFamily: theme.fonts.body,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
  };

  const amenityStyle = (isActive: boolean): React.CSSProperties => ({
    backgroundColor: theme.colors.surface,
    border: isActive ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
    borderRadius: '10px',
    height: '48px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    flex: 1,
    transition: 'all 0.2s',
  });

  const amenityTextStyle = (isActive: boolean): React.CSSProperties => ({
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: isActive ? theme.colors.text : theme.colors.textSecondary,
    fontWeight: isActive ? '600' : 'normal',
  });

  const previewCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const previewTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.text,
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
  };

  const previewRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    fontFamily: theme.fonts.body,
  };

  const previewLabelStyle: React.CSSProperties = {
    color: theme.colors.textSecondary,
  };

  const previewValueStyle: React.CSSProperties = {
    color: theme.colors.text,
    fontWeight: '600',
  };

  const badgeStyle: React.CSSProperties = {
    display: 'inline-block',
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    color: theme.colors.primary,
    padding: '4px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    marginRight: '8px',
    marginBottom: '8px',
  };

  const tipsCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.warning}`,
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const tipsTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.warning,
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
  };

  const tipsTextStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
    lineHeight: '1.6',
    margin: 0,
  };

  const actionBarStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderTop: `1px solid ${theme.colors.border}`,
    padding: '24px 48px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const cancelButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    height: '52px',
    padding: '0 32px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const submitButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    border: 'none',
    borderRadius: '12px',
    height: '52px',
    padding: '0 32px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: '#000000',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: loading ? 0.6 : 1,
  };

  const getSelectedAmenities = () => {
    const amenities = [];
    if (formData.hasParking) amenities.push('Estacionamiento');
    if (formData.hasBathrooms) amenities.push('Baños');
    if (formData.hasChangingRooms) amenities.push('Vestuarios');
    if (formData.hasBuffet) amenities.push('Buffet');
    if (formData.hasProShop) amenities.push('Pro Shop');
    formData.amenities.forEach(a => amenities.push(a));
    return amenities;
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button
          onClick={() => navigate('/owner/dashboard')}
          style={backButtonStyle}
        >
          <ArrowLeft size={20} />
          Volver al Dashboard
        </button>
        <h1 style={titleStyle}>
          {isEditing ? 'EDITAR COMPLEJO' : 'CREAR COMPLEJO DEPORTIVO'}
        </h1>
        <p style={subtitleStyle}>
          {isEditing
            ? 'Modificá la información de tu complejo deportivo'
            : 'Crea un nuevo complejo deportivo y agregá tus canchas'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={formStyle}>
        <div style={twoColumnsStyle}>
          {/* Left Column - Form Fields */}
          <div style={formColumnStyle}>
            {/* Sección 1: Información Básica */}
            <section style={sectionStyle}>
              <h2 style={sectionTitleStyle}>INFORMACIÓN BÁSICA</h2>

              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Nombre del Complejo <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Ej: Complejo Deportivo Central"
                  style={inputStyle}
                />
                {errors.name && <span style={errorStyle}>{errors.name}</span>}
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe tu complejo deportivo..."
                  rows={4}
                  style={textareaStyle}
                />
              </div>
            </section>

            {/* Sección 2: Ubicación */}
            <section style={sectionStyle}>
              <h2 style={sectionTitleStyle}>UBICACIÓN</h2>

              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Dirección <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="Ej: Av. Corrientes 1234, CABA"
                  style={inputStyle}
                />
                {errors.address && <span style={errorStyle}>{errors.address}</span>}
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Link de Google Maps <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.mapsUrl}
                  onChange={(e) => updateField('mapsUrl', e.target.value)}
                  placeholder="https://maps.google.com/?q=..."
                  style={inputStyle}
                />
                <span style={tipsTextStyle}>
                  Copiá el link desde Google Maps para que los jugadores puedan encontrar tu complejo
                </span>
                {errors.mapsUrl && <span style={errorStyle}>{errors.mapsUrl}</span>}
              </div>

              <div style={rowStyle}>
                <div style={{ ...fieldStyle, flex: 1 }}>
                  <label style={labelStyle}>
                    Ciudad <span style={requiredStyle}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="Ej: Buenos Aires"
                    style={inputStyle}
                  />
                  {errors.city && <span style={errorStyle}>{errors.city}</span>}
                </div>

                <div style={{ ...fieldStyle, flex: 1 }}>
                  <label style={labelStyle}>
                    Provincia <span style={requiredStyle}>*</span>
                  </label>
                  <select
                    value={formData.province}
                    onChange={(e) => updateField('province', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">Seleccionar provincia</option>
                    {PROVINCES.map(province => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                  {errors.province && <span style={errorStyle}>{errors.province}</span>}
                </div>
              </div>
            </section>

            {/* Sección 3: Información de Contacto */}
            <section style={sectionStyle}>
              <h2 style={sectionTitleStyle}>INFORMACIÓN DE CONTACTO</h2>

              <div style={fieldStyle}>
                <label style={labelStyle}>Teléfono</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+54 11 1234-5678"
                  style={inputStyle}
                />
              </div>

              <div style={rowStyle}>
                <div style={{ ...fieldStyle, flex: 1 }}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="info@complejo.com"
                    style={inputStyle}
                  />
                </div>

                <div style={{ ...fieldStyle, flex: 1 }}>
                  <label style={labelStyle}>Sitio Web</label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://complejo.com"
                    style={inputStyle}
                  />
                </div>
              </div>
            </section>

            {/* Sección 4: Amenities del Complejo */}
            <section style={sectionStyle}>
              <h2 style={sectionTitleStyle}>AMENITIES DEL COMPLEJO</h2>

              <div style={rowStyle}>
                <div
                  onClick={() => toggleAmenity('hasParking')}
                  style={amenityStyle(formData.hasParking)}
                >
                  {formData.hasParking && <Check size={20} color={theme.colors.primary} />}
                  <Car size={20} color={formData.hasParking ? theme.colors.primary : theme.colors.textSecondary} />
                  <span style={amenityTextStyle(formData.hasParking)}>Estacionamiento</span>
                </div>

                <div
                  onClick={() => toggleAmenity('hasBathrooms')}
                  style={amenityStyle(formData.hasBathrooms)}
                >
                  {formData.hasBathrooms && <Check size={20} color={theme.colors.primary} />}
                  <ShieldCheck size={20} color={formData.hasBathrooms ? theme.colors.primary : theme.colors.textSecondary} />
                  <span style={amenityTextStyle(formData.hasBathrooms)}>Baños</span>
                </div>
              </div>

              <div style={rowStyle}>
                <div
                  onClick={() => toggleAmenity('hasChangingRooms')}
                  style={amenityStyle(formData.hasChangingRooms)}
                >
                  {formData.hasChangingRooms && <Check size={20} color={theme.colors.primary} />}
                  <Home size={20} color={formData.hasChangingRooms ? theme.colors.primary : theme.colors.textSecondary} />
                  <span style={amenityTextStyle(formData.hasChangingRooms)}>Vestuarios</span>
                </div>

                <div
                  onClick={() => toggleAmenity('hasBuffet')}
                  style={amenityStyle(formData.hasBuffet)}
                >
                  {formData.hasBuffet && <Check size={20} color={theme.colors.primary} />}
                  <Coffee size={20} color={formData.hasBuffet ? theme.colors.primary : theme.colors.textSecondary} />
                  <span style={amenityTextStyle(formData.hasBuffet)}>Buffet/Cantina</span>
                </div>
              </div>

              <div style={{ ...rowStyle, maxWidth: '50%' }}>
                <div
                  onClick={() => toggleAmenity('hasProShop')}
                  style={amenityStyle(formData.hasProShop)}
                >
                  {formData.hasProShop && <Check size={20} color={theme.colors.primary} />}
                  <ShoppingBag size={20} color={formData.hasProShop ? theme.colors.primary : theme.colors.textSecondary} />
                  <span style={amenityTextStyle(formData.hasProShop)}>Pro Shop</span>
                </div>
              </div>

              {/* Amenities Adicionales */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Amenities Adicionales</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <input
                    type="text"
                    value={newAmenityInput}
                    onChange={(e) => setNewAmenityInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Wi-Fi, Parrillas, Quincho, etc."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={addAmenity}
                    disabled={!newAmenityInput.trim()}
                    style={{
                      backgroundColor: newAmenityInput.trim() ? theme.colors.primary : theme.colors.border,
                      border: 'none',
                      borderRadius: '10px',
                      height: '48px',
                      padding: '0 24px',
                      fontSize: '14px',
                      fontFamily: theme.fonts.body,
                      color: newAmenityInput.trim() ? '#000000' : theme.colors.textSecondary,
                      fontWeight: '600',
                      cursor: newAmenityInput.trim() ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Agregar
                  </button>
                </div>
                <span style={tipsTextStyle}>
                  Agregá amenities personalizados para tu complejo
                </span>
              </div>

              {/* Custom Amenities Grid */}
              {formData.amenities.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={labelStyle}>Amenities Personalizados</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {formData.amenities.map((amenity, index) => (
                      <div
                        key={index}
                        style={{
                          backgroundColor: theme.colors.surface,
                          border: `2px solid ${theme.colors.primary}`,
                          borderRadius: '10px',
                          height: '48px',
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <Check size={20} color={theme.colors.primary} />
                          <span style={{
                            fontSize: '14px',
                            fontFamily: theme.fonts.body,
                            color: theme.colors.text,
                            fontWeight: '600',
                          }}>
                            {amenity}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAmenity(amenity)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.colors.danger,
                            transition: 'transform 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </div>

          {/* Right Column - Preview & Tips */}
          <div style={sidebarStyle}>
            {/* Preview Card */}
            <div style={previewCardStyle}>
              <h3 style={previewTitleStyle}>RESUMEN</h3>

              <div style={previewRowStyle}>
                <span style={previewLabelStyle}>Nombre</span>
                <span style={previewValueStyle}>
                  {formData.name || 'Sin nombre'}
                </span>
              </div>

              <div style={previewRowStyle}>
                <span style={previewLabelStyle}>Ubicación</span>
                <span style={previewValueStyle}>
                  {formData.city && formData.province
                    ? `${formData.city}, ${formData.province}`
                    : 'Sin ubicación'}
                </span>
              </div>

              <div style={previewRowStyle}>
                <span style={previewLabelStyle}>Estado</span>
                <span style={{ ...previewValueStyle, color: theme.colors.success }}>
                  {isEditing ? (complex?.isActive ? 'Activo' : 'Inactivo') : 'Nuevo'}
                </span>
              </div>

              {getSelectedAmenities().length > 0 && (
                <>
                  <hr style={{ border: 'none', borderTop: `1px solid ${theme.colors.border}`, margin: '8px 0' }} />
                  <div>
                    <span style={previewLabelStyle}>Amenities:</span>
                    <div style={{ marginTop: '12px' }}>
                      {getSelectedAmenities().map((amenity, index) => (
                        <span key={index} style={badgeStyle}>{amenity}</span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Tips Card */}
            <div style={tipsCardStyle}>
              <h3 style={tipsTitleStyle}>💡 CONSEJO</h3>
              <p style={tipsTextStyle}>
                Asegurate de ingresar correctamente la dirección y el link de Google Maps.
                Esto ayudará a los jugadores a encontrar tu complejo fácilmente.
              </p>
              <p style={tipsTextStyle}>
                Una vez creado el complejo, podrás agregar canchas individuales con sus
                características específicas.
              </p>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div style={actionBarStyle}>
          <button
            type="button"
            onClick={() => navigate('/owner/dashboard')}
            style={cancelButtonStyle}
          >
            <X size={20} />
            Cancelar
          </button>

          <button
            type="submit"
            disabled={loading}
            style={submitButtonStyle}
          >
            <Save size={20} />
            {loading ? 'Guardando...' : isEditing ? 'Actualizar Complejo' : 'Guardar Complejo'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateComplexPage;
